/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useEffect, useState } from "react";
import {
  Music, PlayCircle, Gamepad2, Search, MessageSquare,
  LayoutGrid, LogOut, Power, X,
} from "lucide-react";
import { db, auth } from "../firebase-config";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import EditUser from "./EditUser.js";

// Drawer item styling, pinned INLINE on purpose: ManufMuuzbox.scss has a
// blanket `button { padding: .5rem 1rem; margin-left: .5rem; background:
// linear-gradient(#fa163f, #2e6db5) }` under the app root that would
// otherwise paint every row with the logo-red/blue gradient (a brand
// violation in product UI) and shove rows off-grid. Matches the admin PWA
// sidebar's `.nav-item` (flex, gap-3, px-6 py-3, muted → white on hover).
const itemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  width: "100%",
  margin: 0,
  padding: "12px 24px",
  background: "transparent",
  border: "none",
  borderRadius: 0,
  color: "#8892A4",
  fontSize: "16px",
  textAlign: "left",
  cursor: "pointer",
  transition: "all .2s",
};

export const SideBarMenu = ({
  sbm,
  openUsersPanel,
  openJukeboxPanel,
  openAutoPlayPanel,
  openHiddenChatPanel,
  openGamesPanel,
  openChatV2Panel,
  funcProp,
  loadingAni,
  appMode,
  appLayoutMode,
  iconColor,
  userObj,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [runJukebox, setRunJukebox] = useState(false);
  const [canLogout, setCanLogout] = useState(false);
  const menuContainer = useRef(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [jukeboxUserObj, setJukeboxUserObj] = useState(userObj);

  const handleOutsideClick = (e) => {
    if (menuContainer.current && !menuContainer.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (auth.currentUser) {
      setCanLogout(!auth.currentUser.isAnonymous);
      if (!runJukebox) {
        setRunJukebox(true);
        openJukeboxPanel(true);
      }
    }
  }, []);

  // Outside-tap-to-close: attach only while the menu is open, listen for
  // `pointerdown` (unified touch+mouse — fires reliably on modern Android
  // including S25 Ultra, where `click` is not always synthesized for taps
  // on non-interactive areas). Capture phase so we run before nested
  // handlers can stop propagation.
  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener("pointerdown", handleOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick, true);
    };
  }, [isOpen]);

  useEffect(() => {
    // console.log("appMode", appMode);
  }, [appMode]);

  const handleSignOut = async () => {
    //before sign out update the user
    const docRef = doc(db, "users", auth.currentUser.uid);
    const docSnap = await getDoc(docRef);
    const currentChat = { currentChat: "none" };
    if (docSnap.exists()) {
      try {
        await updateDoc(docRef, { ...currentChat });
        sbm("sign out");
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    setJukeboxUserObj(userObj);
  }, [userObj]);

  const showUser = (event, user) => {
    event.preventDefault();
    // console.log("showUser", user);
    setShowUserModal(true);
  };

  return (
    <>
      <div className="flex threedot-container">
        <div className="flex items-center justify-center relative ">
          <div
            className="thredotmenux cursor-pointer text-gray-800 dark:text-gray-800"
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="hamburgermenu"></div>
            <div className="hamburgermenu"></div>
            <div className="hamburgermenu"></div>
          </div>
          {isOpen && (
            // Dimmed, blurred backdrop — same treatment as the Muuzbox PWA
            // sidebar (bg-black/60 + blur). Still a full-viewport pointer
            // target for the S24/S25 Ultra event-chain flakiness (see
            // SideBarMenu.js).
            <div
              className="fixed inset-0 z-[100]"
              style={{
                background: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
                touchAction: "manipulation",
              }}
              onPointerDown={(e) => {
                if (e.target === e.currentTarget) setIsOpen(false);
              }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsOpen(false);
              }}
              aria-hidden="true"
            />
          )}
          {/* Slide-out drawer — the SAME menu style as the Muuzbox PWA
              sidebar (dark glass panel sliding in from the left), replacing
              the old white w-44 dropdown. Always mounted so the transform
              actually animates; hand-written transform because Samsung
              Internet drops transitions driven through Tailwind's
              --tw-translate-x custom property. */}
          <aside
            ref={menuContainer}
            aria-hidden={!isOpen}
            className="fixed top-0 left-0 h-full z-[101] flex flex-col"
            style={{
              width: "23rem",
              maxWidth: "85vw",
              background: "rgba(21,28,46,0.94)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderRight: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "0 5px 5px 0",
              boxShadow: "8px 0 32px rgba(0,0,0,0.4)",
              transform: isOpen ? "translateX(0)" : "translateX(-105%)",
              transition: "transform .3s ease-in-out",
              overflowY: "auto",
            }}
          >
            {/* Header — Muuzbox wordmark-style text + close, as in the PWA */}
            <div
              className="flex items-start justify-between flex-shrink-0"
              style={{ padding: "24px 24px 16px" }}
            >
              <div>
                <div
                  style={{
                    fontFamily: '"Syne", sans-serif',
                    fontWeight: 800,
                    fontSize: "22px",
                    color: "#E8EAF0",
                    lineHeight: 1.1,
                  }}
                >
                  Muuzbox
                </div>
                <div style={{ fontSize: "14px", color: "#8892A4", marginTop: "2px" }}>
                  Player
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Close menu"
                style={{
                  margin: 0,
                  padding: 0,
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "6px",
                  color: "#E8EAF0",
                  cursor: "pointer",
                }}
              >
                <X size={18} />
              </button>
            </div>
            {/* Brand gradient rule under the header */}
            <div
              aria-hidden="true"
              style={{
                height: "2px",
                margin: "0 24px 10px",
                borderRadius: "2px",
                background: "linear-gradient(135deg, #E8007D, #7C3AED)",
                flexShrink: 0,
              }}
            />
            <nav className="flex-1" style={{ paddingBottom: "16px" }}>
              {window.location.hostname !== "admin.muuzbox.com" && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    openJukeboxPanel(true);
                  }}
                  className="hover:bg-white/5 hover:text-white nodecor"
                  style={itemStyle}
                >
                  <Music size={18} /> Jukebox
                </button>
              )}
              {(!auth.currentUser.isAnonymous ||
                (appMode && appMode.login === "true")) && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    openAutoPlayPanel(true);
                  }}
                  className="hover:bg-white/5 hover:text-white nodecor"
                  style={itemStyle}
                >
                  <PlayCircle size={18} /> Auto Play
                </button>
              )}
              {openGamesPanel && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    openGamesPanel(true);
                  }}
                  className="hover:bg-white/5 hover:text-white nodecor"
                  style={itemStyle}
                >
                  <Gamepad2 size={18} /> Games
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  openUsersPanel(true);
                }}
                className="hover:bg-white/5 hover:text-white nodecor"
                style={itemStyle}
              >
                <Search size={18} /> Search Members
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  // My Chats = the rebuilt chat, everywhere. The legacy chat
                  // surface is retired (2026-08-31 verdict).
                  if (openChatV2Panel) openChatV2Panel();
                  else { loadingAni(); openHiddenChatPanel(true); }
                }}
                className="hover:bg-white/5 hover:text-white nodecor"
                style={itemStyle}
              >
                <MessageSquare size={18} /> My Chats
              </button>
              <button
                onClick={() => {
                  appLayoutMode("newlayout");
                }}
                className="hover:bg-white/5 hover:text-white nodecor"
                style={itemStyle}
              >
                <LayoutGrid size={18} /> Switch Layout
              </button>
              <button
                id="notifyMuuzbox-close3"
                onClick={() => {
                  window.close();
                }}
                className="hover:bg-white/5 hover:text-white nodecor"
                style={itemStyle}
              >
                <Power size={18} /> Close Muuzbox
              </button>
              {(!auth.currentUser.isAnonymous ||
                (appMode && appMode.login === "true")) && (
                <button
                  onClick={() => handleSignOut()}
                  className="hover:bg-red-500/10 nodecor"
                  style={{ ...itemStyle, color: "#f87171" }}
                >
                  <LogOut size={18} /> Sign out
                </button>
              )}
            </nav>
            <div
              className="flex-shrink-0"
              style={{
                padding: "12px 24px",
                borderTop: "1px solid rgba(255,255,255,0.08)",
                color: "#8892A4",
                fontSize: "13px",
              }}
            >
              <strong>v 0.5.6</strong>
            </div>
          </aside>
        </div>
      </div>
      {showUserModal && (
        <EditUser
          userObj={userObj}
          setShowUserModal={setShowUserModal}
          placeholderAvatar={jukeboxUserObj.photoURL}
        />
      )}
    </>
  );
};
